<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import { useQuery, useQueryClient } from "@tanstack/vue-query";

import {
  cancelInvitation,
  createInvitation,
  createWorker,
  getComplianceDashboard,
  getStaffingSummary,
  importNameSheet,
  importRoster,
  listInvitations,
  listWorkers,
} from "@/api/client";
import { env } from "@/env";
import PageLayout from "@/components/PageLayout.vue";
import WorkerAvatar from "@/components/WorkerAvatar.vue";

const queryClient = useQueryClient();

const { data: staffing } = useQuery({
  queryKey: ["staffing-summary"],
  queryFn: () => getStaffingSummary(),
});

const { data: workers, isPending, isError, error } = useQuery({
  queryKey: ["workers"],
  queryFn: () => listWorkers(),
});
const { data: compliance } = useQuery({
  queryKey: ["compliance-dashboard"],
  queryFn: () => getComplianceDashboard(),
});
const { data: invites } = useQuery({
  queryKey: ["invitations"],
  queryFn: () => listInvitations(),
});

// ---- Needs attention (from the compliance dashboard, already loaded) ----
interface AttentionItem {
  workerId: string;
  workerName: string;
  label: string; // what's expiring
  detail: string; // e.g. "expires in 12d" / "expired"
  daysLeft: number; // for sorting; expired sorts first (negative)
  severity: "expired" | "expiring";
}

const attention = computed<AttentionItem[]>(() => {
  const d = compliance.value;
  if (!d) return [];
  const items: AttentionItem[] = [];
  const cardItem = (
    c: (typeof d.expired_cards)[number],
    severity: "expired" | "expiring",
  ): AttentionItem => ({
    workerId: c.worker_id,
    workerName: c.worker_name,
    label: c.category ?? c.alias ?? "Competence",
    detail:
      c.days_left == null
        ? severity
        : c.days_left < 0
          ? "expired"
          : c.days_left === 0
            ? "expires today"
            : `expires in ${c.days_left}d`,
    daysLeft: c.days_left ?? 0,
    severity,
  });
  for (const c of d.expired_cards) items.push(cardItem(c, "expired"));
  for (const c of d.expiring_cards) items.push(cardItem(c, "expiring"));
  for (const m of d.medicals_attention) {
    const expired = !m.reason.toLowerCase().includes("no medical");
    items.push({
      workerId: m.worker_id,
      workerName: m.worker_name,
      label: "Medical",
      detail: m.reason,
      daysLeft: expired ? -1 : 0,
      severity: expired ? "expired" : "expiring",
    });
  }
  return items.sort((a, b) => a.daysLeft - b.daysLeft);
});
const showAllAttention = ref(false);
const visibleAttention = computed(() =>
  showAllAttention.value ? attention.value : attention.value.slice(0, 6),
);

const search = ref("");
const showInactive = ref(false);
// When set, the table shows only workers not on any project (from the staffing
// panel's "not on any project" count).
const unassignedOnly = ref(false);
const unassignedIds = computed(
  () => new Set(staffing.value?.unassigned_worker_ids ?? []),
);

const filtered = computed(() => {
  const q = search.value.trim().toLowerCase();
  return (workers.value ?? []).filter((w) => {
    if (!showInactive.value && !w.active) return false;
    if (unassignedOnly.value && !unassignedIds.value.has(w.id)) return false;
    if (!q) return true;
    return (
      w.name.toLowerCase().includes(q) ||
      w.ncca_no.toLowerCase().includes(q) ||
      (w.phone ?? "").toLowerCase().includes(q)
    );
  });
});

function expiresIn(iso: string): string {
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
  if (days < 0) return "expired";
  if (days === 0) return "today";
  return `in ${days}d`;
}

// ---- New worker ----
const showNew = ref(false);
const newW = reactive({ ncca_no: "", name: "", primary_role: "" });
const newBusy = ref(false);
const newError = ref<string | null>(null);
async function submitWorker(): Promise<void> {
  if (!newW.ncca_no.trim() || !newW.name.trim()) return;
  newBusy.value = true;
  newError.value = null;
  try {
    await createWorker({
      ncca_no: newW.ncca_no.trim(),
      name: newW.name.trim(),
      primary_role: newW.primary_role.trim() || undefined,
    });
    await queryClient.invalidateQueries({ queryKey: ["workers"] });
    showNew.value = false;
    newW.ncca_no = newW.name = newW.primary_role = "";
  } catch (e) {
    newError.value = e instanceof Error ? e.message : String(e);
  } finally {
    newBusy.value = false;
  }
}

// ---- Invite worker ----
const showInvite = ref(false);
const inv = reactive({ email: "", worker_name: "", site: "" });
const invBusy = ref(false);
const invError = ref<string | null>(null);
async function submitInvite(): Promise<void> {
  if (!inv.email.trim()) return;
  invBusy.value = true;
  invError.value = null;
  try {
    await createInvitation({
      email: inv.email.trim(),
      worker_name: inv.worker_name.trim() || undefined,
      site: inv.site.trim() || undefined,
    });
    await queryClient.invalidateQueries({ queryKey: ["invitations"] });
    showInvite.value = false;
    inv.email = inv.worker_name = inv.site = "";
  } catch (e) {
    invError.value = e instanceof Error ? e.message : String(e);
  } finally {
    invBusy.value = false;
  }
}

const copied = ref<string | null>(null);
async function copyLink(id: string): Promise<void> {
  if (!env.mobileInstallUrl) {
    window.alert(
      "No app install link is configured. Set VITE_MOBILE_INSTALL_URL to your " +
        "TestFlight link, or share the install link manually.",
    );
    return;
  }
  await navigator.clipboard.writeText(env.mobileInstallUrl);
  copied.value = id;
  setTimeout(() => (copied.value = null), 2000);
}

async function dropInvite(id: string): Promise<void> {
  await cancelInvitation(id);
  await queryClient.invalidateQueries({ queryKey: ["invitations"] });
}

// ---- Import ----
const importing = ref(false);
const importMessage = ref<string | null>(null);
const importError = ref<string | null>(null);
async function onFile(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;
  importing.value = true;
  importMessage.value = null;
  importError.value = null;
  try {
    const w = await importNameSheet(file);
    const roster = await importRoster(file);
    importMessage.value =
      `Imported ${w.workers_imported} workers and ${roster.weeks_imported} ` +
      `roster weeks (${roster.assignments_imported} assignments).`;
    await queryClient.invalidateQueries({ queryKey: ["workers"] });
    await queryClient.invalidateQueries({ queryKey: ["rosters"] });
    await queryClient.invalidateQueries({ queryKey: ["staffing-summary"] });
  } catch (e) {
    importError.value = e instanceof Error ? e.message : String(e);
  } finally {
    importing.value = false;
    input.value = "";
  }
}
</script>

<template>
  <PageLayout wide>
    <div class="grid gap-8">
      <header class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 class="text-2xl font-extrabold text-neutral-900">
            Workforce
          </h1>
          <p class="mt-1 max-w-2xl text-neutral-500">
            Workers, competencies, and supporting documents. Org-wide; assign to
            projects from the project view.
          </p>
        </div>
        <div class="flex gap-2">
          <button
            type="button"
            class="rounded-md border border-neutral-300 px-4 py-2 text-sm font-medium hover:bg-neutral-50"
            @click="showInvite = true"
          >
            Invite worker
          </button>
          <button
            type="button"
            class="rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on hover:opacity-90"
            @click="showNew = true"
          >
            + New worker
          </button>
        </div>
      </header>

      <!-- Compliance stats strip -->
      <section
        v-if="compliance"
        class="grid gap-3"
      >
        <div class="grid grid-cols-3 gap-3">
          <div class="rounded-xl border border-neutral-200 bg-neutral-0 p-4">
            <p class="text-2xl font-bold text-neutral-900">
              {{ compliance.stats.active_workers }}
            </p>
            <p class="text-xs text-neutral-500">
              Active
            </p>
          </div>
          <div class="rounded-xl border border-neutral-200 bg-neutral-0 p-4">
            <p
              class="text-2xl font-bold"
              :class="compliance.stats.cards_expiring ? 'text-semantic-warning' : 'text-neutral-900'"
            >
              {{ compliance.stats.cards_expiring }}
            </p>
            <p class="text-xs text-neutral-500">
              Expiring soon
            </p>
          </div>
          <div class="rounded-xl border border-neutral-200 bg-neutral-0 p-4">
            <p
              class="text-2xl font-bold"
              :class="compliance.stats.cards_expired ? 'text-semantic-danger' : 'text-neutral-900'"
            >
              {{ compliance.stats.cards_expired }}
            </p>
            <p class="text-xs text-neutral-500">
              Expired
            </p>
          </div>
        </div>
      </section>

      <!-- Project staffing — who's where -->
      <section
        v-if="staffing && staffing.projects.length"
        class="rounded-xl border border-neutral-200 bg-neutral-0 p-6"
      >
        <div class="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h2 class="font-semibold text-neutral-900">
            Project staffing
          </h2>
          <p class="text-sm text-neutral-500">
            {{ staffing.assigned_workers }} of
            {{ staffing.total_active_workers }} workers assigned
            <button
              v-if="staffing.unassigned_workers"
              type="button"
              class="font-medium text-semantic-warning hover:underline"
              @click="unassignedOnly = true"
            >
              · {{ staffing.unassigned_workers }} not on any project →
            </button>
          </p>
        </div>
        <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <button
            v-for="p in staffing.projects"
            :key="p.project_id"
            type="button"
            class="flex items-center justify-between gap-3 rounded-lg border border-neutral-200 px-4 py-3 text-left transition hover:border-brand-primary hover:bg-neutral-50"
            @click="$router.push({ name: 'project-team', params: { projectId: p.project_id } })"
          >
            <span class="min-w-0">
              <span class="block truncate font-medium text-neutral-900">
                {{ p.project_name }}
              </span>
              <span class="text-xs text-neutral-400">
                {{ p.worker_count === 1 ? "1 worker" : `${p.worker_count} workers` }}
                <span
                  v-if="p.status !== 'active'"
                  class="capitalize"
                >· {{ p.status }}</span>
              </span>
            </span>
            <span
              class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold"
              :class="p.worker_count ? 'bg-brand-primary/10 text-brand-primary' : 'bg-neutral-100 text-neutral-400'"
            >
              {{ p.worker_count }}
            </span>
          </button>
        </div>
        <p class="mt-3 text-xs text-neutral-400">
          Click a project to assign or remove workers on its Team page.
        </p>
      </section>

      <!-- Needs attention -->
      <section
        v-if="attention.length"
        class="rounded-xl border border-semantic-warning/30 bg-semantic-warning/5 p-6"
      >
        <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 class="font-semibold text-neutral-900">
            Needs attention
            <span class="text-neutral-400">({{ attention.length }})</span>
          </h2>
          <RouterLink
            to="/compliance"
            class="text-sm text-brand-primary hover:underline"
          >
            Full compliance view →
          </RouterLink>
        </div>
        <ul class="grid gap-1">
          <li
            v-for="(a, i) in visibleAttention"
            :key="i"
            class="flex cursor-pointer items-center gap-3 rounded-md border-b border-neutral-100/60 py-1.5 last:border-0 hover:bg-neutral-0/60"
            @click="$router.push(`/workers/${a.workerId}`)"
          >
            <span
              class="h-2 w-2 shrink-0 rounded-full"
              :class="a.severity === 'expired' ? 'bg-semantic-danger' : 'bg-semantic-warning'"
            />
            <span class="min-w-0 grow truncate text-sm">
              <span class="font-medium text-neutral-900">{{ a.workerName }}</span>
              <span class="text-neutral-500"> · {{ a.label }}</span>
            </span>
            <span
              class="shrink-0 text-xs font-medium"
              :class="a.severity === 'expired' ? 'text-semantic-danger' : 'text-semantic-warning'"
            >
              {{ a.detail }}
            </span>
          </li>
        </ul>
        <button
          v-if="attention.length > 6"
          type="button"
          class="mt-2 text-sm text-brand-primary hover:underline"
          @click="showAllAttention = !showAllAttention"
        >
          {{ showAllAttention ? "Show less" : `Show all ${attention.length}` }}
        </button>
      </section>

      <!-- Pending invites -->
      <section
        v-if="invites && invites.length"
        class="rounded-xl border border-neutral-200 bg-neutral-0 p-6"
      >
        <h2 class="mb-3 font-semibold">
          Pending invites <span class="text-neutral-400">({{ invites.length }})</span>
        </h2>
        <ul class="grid gap-2">
          <li
            v-for="i in invites"
            :key="i.id"
            class="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-50 pb-2 last:border-0"
          >
            <div class="text-sm">
              <span class="font-medium text-neutral-900">{{ i.email }}</span>
              <span
                v-if="i.worker_name"
                class="text-neutral-500"
              > → {{ i.worker_name }}</span>
              <span
                v-if="i.site"
                class="text-neutral-400"
              > · on {{ i.site }}</span>
              <span class="block text-xs text-neutral-400">
                expires {{ expiresIn(i.expires_at) }}<span v-if="i.invited_by_name"> · invited by {{ i.invited_by_name }}</span>
              </span>
            </div>
            <div class="flex items-center gap-3 text-sm">
              <button
                type="button"
                class="text-brand-primary hover:underline"
                @click="copyLink(i.id)"
              >
                {{ copied === i.id ? "Copied!" : "Copy link" }}
              </button>
              <button
                type="button"
                class="text-neutral-400 hover:text-semantic-danger"
                @click="dropInvite(i.id)"
              >
                Cancel
              </button>
            </div>
          </li>
        </ul>
      </section>

      <!-- Import -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <h2 class="mb-2 font-semibold">
          Import roster workbook
        </h2>
        <p class="mb-4 text-sm text-neutral-500">
          Upload the OLE roster <code>.xlsm</code>; the Name sheet seeds the
          worker master and the week tabs become weekly rosters.
        </p>
        <label
          class="inline-flex cursor-pointer items-center rounded-md border border-neutral-300 px-4 py-2 text-sm font-medium hover:bg-neutral-50"
          :class="{ 'pointer-events-none opacity-50': importing }"
        >
          {{ importing ? "Importing…" : "Choose workbook…" }}
          <input
            type="file"
            accept=".xlsm,.xlsx"
            class="hidden"
            :disabled="importing"
            @change="onFile"
          >
        </label>
        <p
          v-if="importMessage"
          class="mt-3 text-sm text-semantic-success"
        >
          {{ importMessage }}
        </p>
        <p
          v-if="importError"
          class="mt-3 text-sm text-semantic-danger"
        >
          {{ importError }}
        </p>
      </section>

      <!-- Workers list -->
      <div
        v-if="isPending"
        class="text-neutral-500"
      >
        Loading…
      </div>
      <div
        v-else-if="isError"
        class="rounded-md bg-semantic-danger/10 p-4 text-semantic-danger"
      >
        Failed to load: {{ error?.message }}
      </div>
      <div
        v-else
        class="grid gap-4"
      >
        <div class="flex flex-wrap items-center gap-4">
          <input
            v-model="search"
            type="search"
            placeholder="Search name, NCCA or phone…"
            class="grow rounded-md border border-neutral-300 px-3 py-2 text-sm"
          >
          <button
            v-if="unassignedOnly"
            type="button"
            class="inline-flex items-center gap-1.5 rounded-full bg-semantic-warning/10 px-3 py-1.5 text-sm font-medium text-semantic-warning"
            @click="unassignedOnly = false"
          >
            Not on any project
            <span class="text-base leading-none">×</span>
          </button>
          <label class="flex items-center gap-2 text-sm text-neutral-500">
            <input
              v-model="showInactive"
              type="checkbox"
            >
            Show inactive
          </label>
        </div>

        <div class="overflow-hidden rounded-xl border border-neutral-200">
          <table class="w-full text-left text-sm">
            <thead class="bg-neutral-50 text-neutral-500">
              <tr>
                <th class="px-4 py-2 font-medium">
                  Name
                </th>
                <th class="px-4 py-2 font-medium">
                  Role
                </th>
                <th class="px-4 py-2 font-medium">
                  NCCA
                </th>
                <th class="px-4 py-2 font-medium">
                  Phone
                </th>
                <th class="px-4 py-2 font-medium">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="w in filtered"
                :key="w.id"
                class="cursor-pointer border-t border-neutral-100 hover:bg-neutral-50"
                @click="$router.push(`/workers/${w.id}`)"
              >
                <td class="px-4 py-2 font-medium text-neutral-900">
                  <span class="flex items-center gap-3">
                    <WorkerAvatar
                      :name="w.name"
                      :file-id="w.image_file_id"
                      :size="32"
                    />
                    {{ w.name }}
                  </span>
                </td>
                <td class="px-4 py-2 text-neutral-500">
                  {{ w.primary_role ?? "—" }}
                </td>
                <td class="px-4 py-2 text-neutral-500">
                  {{ w.ncca_no }}
                </td>
                <td class="px-4 py-2 text-neutral-500">
                  {{ w.phone ?? "—" }}
                </td>
                <td class="px-4 py-2">
                  <span :class="w.active ? 'text-semantic-success' : 'text-neutral-400'">
                    {{ w.active ? "Active" : "Inactive" }}
                  </span>
                </td>
              </tr>
              <tr v-if="!filtered.length">
                <td
                  colspan="5"
                  class="px-4 py-6 text-center text-neutral-400"
                >
                  No workers — import the roster, add one, or invite a worker.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- New worker modal -->
    <div
      v-if="showNew"
      class="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/40 p-4"
      @click.self="showNew = false"
    >
      <div class="w-full max-w-md rounded-xl bg-neutral-0 p-6 shadow-xl">
        <h2 class="mb-4 font-semibold">
          New worker
        </h2>
        <div class="grid gap-3">
          <input
            v-model="newW.name"
            placeholder="Full name"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
          >
          <input
            v-model="newW.ncca_no"
            placeholder="NCCA number"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
          >
          <input
            v-model="newW.primary_role"
            placeholder="Primary role (optional)"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
          >
        </div>
        <p
          v-if="newError"
          class="mt-2 text-sm text-semantic-danger"
        >
          {{ newError }}
        </p>
        <div class="mt-4 flex justify-end gap-2">
          <button
            type="button"
            class="rounded-md px-4 py-2 text-sm text-neutral-500 hover:bg-neutral-50"
            @click="showNew = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
            :disabled="newBusy || !newW.name.trim() || !newW.ncca_no.trim()"
            @click="submitWorker"
          >
            {{ newBusy ? "Creating…" : "Create worker" }}
          </button>
        </div>
      </div>
    </div>

    <!-- Invite worker modal -->
    <div
      v-if="showInvite"
      class="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/40 p-4"
      @click.self="showInvite = false"
    >
      <div class="w-full max-w-md rounded-xl bg-neutral-0 p-6 shadow-xl">
        <h2 class="mb-1 font-semibold">
          Invite worker
        </h2>
        <p class="mb-4 text-sm text-neutral-500">
          Records the invite and gives you the app install link to send them.
        </p>
        <div class="grid gap-3">
          <input
            v-model="inv.email"
            type="email"
            placeholder="Email"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
          >
          <input
            v-model="inv.worker_name"
            placeholder="Name (optional)"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
          >
          <input
            v-model="inv.site"
            placeholder="Site (optional)"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
          >
        </div>
        <p
          v-if="invError"
          class="mt-2 text-sm text-semantic-danger"
        >
          {{ invError }}
        </p>
        <div class="mt-4 flex justify-end gap-2">
          <button
            type="button"
            class="rounded-md px-4 py-2 text-sm text-neutral-500 hover:bg-neutral-50"
            @click="showInvite = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
            :disabled="invBusy || !inv.email.trim()"
            @click="submitInvite"
          >
            {{ invBusy ? "Inviting…" : "Send invite" }}
          </button>
        </div>
      </div>
    </div>
  </PageLayout>
</template>
